import { useCallback, useRef } from 'react';

import { v4 as uuid } from 'uuid';

import { createWithOverrides } from '../overrides';

import { useMSRNudgeRecommendations, useMsrSnoozeHandler } from './utils';

type EligibilityCheck = { isEligible: boolean; ineligibleReason?: string };

type Nudge = {
	nudgeEligibility: () => Promise<EligibilityCheck>;
	nudgeKey: string;
	showNudge: () => void;
};

/*
  Eligibility can be overridden using the following query parameter:

  ?cjn_msr_overrides={
    "feature_visited_too_recently": false,
  }
*/
type OverrideKey = 'feature_visited_too_recently';

export const useExecuteMSRNudge = ({
	cloudId,
	product,
	subProduct,
	onNoMsrResultsReturned,
	onEligibilityReturned,
	onError,
	getFeatureVisited,
	setFeatureVisited,
	getFeatureVisitedCount,
	setFeatureVisitedCount,
	overrides,
}: {
	cloudId: string;
	getFeatureVisited: () => boolean;
	getFeatureVisitedCount: () => number;
	onEligibilityReturned: ({
		eligibilityResult,
		key,
	}: {
		eligibilityResult: EligibilityCheck;
		key: string;
	}) => void;
	onNoMsrResultsReturned: () => void;
	onError: (err: unknown) => void;
	overrides: Partial<Record<OverrideKey, boolean>>;
	product: string;
	setFeatureVisited: (days: number) => void;
	setFeatureVisitedCount: (count: number) => void;
	subProduct: string;
}) => {
	const sessionId = useRef<string>(uuid());
	const getMSRNudgeRecommendations = useMSRNudgeRecommendations({
		product,
		sessionId: sessionId.current,
		cloudId,
		subProduct,
	});
	const { blockEligibilityUntilTomorrow, snooze } = useMsrSnoozeHandler({
		setFeatureVisited,
		setFeatureVisitedCount,
		getFeatureVisitedCount,
	});
	const withOverrides = createWithOverrides(overrides);

	return useCallback(
		async ({ nudges }: { nudges: Nudge[] }) => {
			try {
				if (withOverrides('feature_visited_too_recently', getFeatureVisited())) {
					return;
				}

				const eligibilityResults: (Nudge & { isEligible: boolean })[] = await Promise.all(
					nudges.map(async (nudge) => {
						const eligibilityResult = await nudge.nudgeEligibility();
						onEligibilityReturned({ eligibilityResult, key: nudge.nudgeKey });
						return { ...nudge, ...eligibilityResult };
					}),
				);

				const eligibleNudges = eligibilityResults.filter((result) => result.isEligible);

				if (eligibleNudges.length <= 0) {
					return;
				}

				const recommendations = await getMSRNudgeRecommendations(
					nudges.map((nudge) => nudge.nudgeKey),
				);

				if (recommendations.length <= 0) {
					blockEligibilityUntilTomorrow();
					onNoMsrResultsReturned();
					return;
				}

				for (const recommendation of recommendations) {
					const nudge = eligibleNudges.find((nudge) => nudge.nudgeKey === recommendation);
					if (nudge) {
						snooze();
						return nudge.showNudge();
					}
				}
			} catch (error: unknown) {
				blockEligibilityUntilTomorrow();
				onError(error);
			}
		},
		[
			getFeatureVisited,
			getMSRNudgeRecommendations,
			blockEligibilityUntilTomorrow,
			withOverrides,
			snooze,
			onError,
			onNoMsrResultsReturned,
			onEligibilityReturned,
		],
	);
};
